import * as React from "react";
import { observer, Provider, inject } from "mobx-react";
import Editor, { InferenceMode } from "../../editor/Editor";
import EditorView from "../../editor-ui/EditorView";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  AlertColor,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LogoutButton from "../common/LogoutButton";
import autobind from "autobind-decorator";
import AccountService from "../../service/AccountService";
import { RouterStore } from "mobx-react-router";
import { observable } from "mobx";
import FileService from "../../service/FileService";
import {
  deserializeLayerGroup,
  serializeLayerGroup,
} from "../../editor/serializer";
import SaveButton from "./SaveButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NodeLayer from "../../editor/NodeLayer";
import { createTransformToolChain } from "../../editor/toolChains";
import MailIcon from "@mui/icons-material/Mail";
import ShareDialog from "./ShareDialog";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import SaveDialog from "./SaveDialog";
import BackIcon from "@mui/icons-material/ArrowBack";
import { withStyles } from "@mui/styles";
import RunningWithErrorsIcon from "@mui/icons-material/RunningWithErrors";
import UpdateIcon from "@mui/icons-material/Update";
import moment from "moment";

const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100%",
});

const LoaderContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  alignItems: "center",
  justifyContent: "center",
});

const EditIconBtn = withStyles({
  root: {
    color: "#FFF",
  },
})(IconButton);

const MobileHideDiv = styled("div")({
  display: "flex",
  flexDirection: "row",
  "@media screen and (max-width: 900px)": {
    display: "none",
  },
});
const BrowserHideDiv = styled("div")({
  "@media screen and (min-width: 900px)": {
    display: "none",
  },
});
const NavButton = withStyles({
  colorInherit: {
    color: "#FFF",
  },
})(Button);

type EditorPageProps = {
  routing?: RouterStore;
  account?: AccountService;
  files?: FileService;
  match?: any;
};

type EditorPageStatus = "LOADING" | "NORMAL";

@inject("account")
@inject("routing")
@inject("files")
@observer
export default class EditorPage extends React.Component<EditorPageProps> {
  private _editor = new Editor();

  @observable
  private _status: EditorPageStatus = "LOADING";

  @observable
  private _fileName = "";

  @observable
  private anchorElMain = null;

  @observable
  private anchorElPreview = null;

  @observable
  private _showSnackbar: boolean = false;

  @observable
  private _snackbarMessage: string = "";

  @observable
  private _snackbarType: AlertColor = "success";

  @observable
  private fileId = null;

  @observable
  private isShareDialog: boolean = false;

  @observable
  private isLoading: boolean = false;

  @observable
  private isSaveDialog: boolean = false;

  // @observable
  // private _selectedFileName: string = "";

  // @observable
  // snackMessage = "";

  @autobind
  goBack() {
    var { account } = this.props;
    this.saveData();
    account.goBack();
  }

  @autobind
  gotoPlan() {
    var { routing } = this.props;
    routing.push(`/plan`);
  }

  @autobind
  async startFreeTrialHandler() {
    var { routing, account } = this.props;
    const result = await account.startFreeTrial(account.user.uid);
    if (result === "SUCCESS") {
      this._showSnackbar = true;
      this._snackbarMessage = "Free trial started successfully";
      this._snackbarType = "success";
      routing.replace(`/editor/${this.fileId}`);
    } else {
      this._showSnackbar = true;
      this._snackbarMessage = "Something went wrong";
      this._snackbarType = "error";
    }
  }

  handleMoreClick(event: React.MouseEvent<HTMLButtonElement>) {
    this.anchorElMain = event.currentTarget;
  }

  @autobind
  handleMoreClose() {
    this.anchorElMain = null;
  }
  handlePreviewMoreClick(event: React.MouseEvent<HTMLButtonElement>) {
    this.anchorElPreview = event.currentTarget;
  }

  @autobind
  handlePreviewMoreClose() {
    this.anchorElPreview = null;
  }

  @autobind
  gotoNarratives() {
    var { routing } = this.props;
    routing.push(`/narratives/${this.fileId}`);
  }

  getSurveyLink() {
    var { account } = this.props;
    if (account.user?.uid) {
      return `/questionnaire/${account.user.uid}/${account.currentClinic}/${this.fileId}`;
    } else {
      return "/";
    }
  }

  @autobind
  async gotoQuestionnaire() {
    var { routing } = this.props;
    await this.saveData();
    routing.push(this.getSurveyLink());
  }

  handleInference(mode: InferenceMode) {
    if (mode == "StartLayer" && this._editor.startLayer == null) {
      this._editor.setError({
        title: "OOPS!",
        value: "Please create a start question.",
      });
      return;
    }

    if (mode == "SelectedLayer") {
      if (
        this._editor.selectedLayer == null ||
        !(this._editor.selectedLayer instanceof NodeLayer)
      ) {
        this._editor.setError({
          title: "OOPS!",
          value: "Please select a question to begin.",
        });
        return;
      }
    }

    for (var layer of this._editor.rootLayerGroup.layers) {
      if (layer instanceof NodeLayer && layer.narrativeCompileError != "") {
        this._editor.setSelectedLayer(layer);
        this._editor.setCurrentTool(createTransformToolChain(this._editor));

        this._editor.setError({
          title: "OOPS!",
          value: `There is a narrative template compile error in question: ${layer.questionText}`,
        });
        return;
      }
    }

    this._editor.setInferenceMode(mode);
  }

  @autobind
  async componentDidMount() {
    const { id } = this.props.match.params;
    const { routing, files } = this.props;
    this.fileId = id;
    if (id == null) {
      routing.replace("/");
      return;
    }
    this._status = "LOADING";
    var fileData = await files.loadFileData(id);
    var fileName = await files.loadFileName(id);

    if (fileData == "UNKNOWN_ERROR" || fileName == "UNKNOWN_ERROR") {
      routing.replace("/");
      return;
    }

    this._fileName = fileName;

    this._editor = new Editor();

    if (fileData != "") {
      deserializeLayerGroup(fileData, this._editor);
    }

    this._status = "NORMAL";

    window.onbeforeunload = function () {
      return null;
      //return "Changes you made may not be saved. Are you sure?";
    };
  }

  componentWillUnmount() {
    window.onbeforeunload = null;
  }

  @autobind
  async saveData(event: any = null): Promise<void> {
    if (event != null) {
      event.preventDefault();
    }

    if (this._editor == null) {
      return;
    }

    var fileData = serializeLayerGroup(this._editor);

    let { files } = this.props;
    const { id } = this.props.match.params;

    var res = await files.saveFileData(id, fileData);
    var saveBackup = await files.saveFileDataBackup(id, fileData);
  }

  @autobind
  async openShareDialog() {
    await this.saveData();
    this.isShareDialog = true;
  }

  @autobind
  async checkChanges() {
    this.isLoading = true;
    const { id } = this.props.match.params;
    const { routing, files, account } = this.props;
    if (id == null) {
      routing.replace("/");
      return;
    }
    const previousFileData = await files.loadFileData(id);
    const newFileData = serializeLayerGroup(this._editor);
    if (previousFileData === newFileData) account.goBack();
    else {
      this.isLoading = false;
      this.isSaveDialog = true;
    }
  }

  renderNavBar() {
    const userData = this.props.account.userData;
    const userType = this.props.account.userData.userType;
    const startDateOfFreeTrial = moment(userData?.freeTrialStartDate);
    const endDateOf7DayPeriod = startDateOfFreeTrial.clone().add(7, "days");
    const daysLeftInTrial = endDateOf7DayPeriod.diff(moment(), "days");

    return (
      <AppBar position="static">
        <Toolbar variant="dense">
          {/* <SaveButton
            handleSave={() => (this.isSaveDialog = true)}
            kind="back"
          /> */}
          <IconButton
            // onClick={() => (this.isSaveDialog = true)}
            onClick={this.checkChanges}
            style={{ cursor: "pointer", color: "inherit" }}
          >
            {this.isLoading ? (
              <CircularProgress
                size={20}
                style={{
                  color: "white",
                }}
              />
            ) : (
              <BackIcon />
            )}
          </IconButton>

          <Typography variant="h6" style={{ flexGrow: 1, color: "inherit" }}>
            {this._status == "LOADING" ? "Loading..." : this._fileName}
          </Typography>

          <BrowserHideDiv>
            <IconButton
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              onClick={(e) => this.handleMoreClick(e)}
            >
              <MoreVertIcon style={{ color: "#FFF" }} />
            </IconButton>
            <Menu
              id="main-menu"
              anchorEl={this.anchorElMain}
              keepMounted
              open={Boolean(this.anchorElMain)}
              onClose={this.handleMoreClose}
            >
              <MenuItem
                style={{ padding: "6px 0px" }}
                onClick={this.handleMoreClose}
              >
                <Button
                  style={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={() => this.handleInference("StartLayer")}
                >
                  <VisibilityIcon style={{ marginRight: "10px" }} /> PREVIEW
                  FROM BEGINNING{" "}
                </Button>
              </MenuItem>
              <MenuItem
                style={{ padding: "6px 0px" }}
                onClick={this.handleMoreClose}
              >
                <Button
                  style={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={() => this.handleInference("SelectedLayer")}
                >
                  <VisibilityIcon style={{ marginRight: "10px" }} /> PREVIEW
                  FROM SELECTED QUESTION
                </Button>
              </MenuItem>
              <MenuItem
                style={{ padding: "6px 0px" }}
                onClick={this.handleMoreClose}
              >
                <Button
                  style={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={this.gotoQuestionnaire}
                >
                  <i
                    className="material-icons"
                    style={{ transform: "scale(1.2)", marginRight: "10px" }}
                  >
                    {" "}
                    play_arrow{" "}
                  </i>
                  PLAY FOR RESPONDENT
                </Button>
              </MenuItem>
              <MenuItem
                style={{ padding: "6px 0px" }}
                onClick={this.handleMoreClose}
              >
                <Button
                  style={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={this.gotoNarratives}
                >
                  <MailIcon style={{ marginRight: "10px" }} />
                  VIEW RESPONSES
                </Button>
              </MenuItem>
              <MenuItem
                style={{ padding: "6px 0px" }}
                onClick={this.handleMoreClose}
              >
                <Button
                  style={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={this.openShareDialog}
                >
                  <PlayArrowIcon
                    style={{ transform: "scale(1.2)", marginRight: "10px" }}
                  />
                  SHARE
                </Button>
              </MenuItem>
              <MenuItem
                style={{ padding: "6px 0px" }}
                onClick={this.handleMoreClose}
              >
                <SaveButton handleSave={this.saveData} kind="save" />
              </MenuItem>
              <MenuItem
                style={{ padding: "6px 0px" }}
                onClick={this.handleMoreClose}
              >
                <LogoutButton view="EDIT" />
              </MenuItem>
            </Menu>
          </BrowserHideDiv>

          <MobileHideDiv>
            {userType == "FREE_USER" &&
              userData?.freeTrialStartDate &&
              daysLeftInTrial > 0 && (
                <NavButton
                  aria-label="menu"
                  onClick={this.gotoPlan}
                  color="inherit"
                  style={{
                    color: "#fffd02",
                  }}
                >
                  <RunningWithErrorsIcon style={{ marginRight: "10px" }} />
                  {`Trial: ${daysLeftInTrial} days left. Upgrade now!`}
                </NavButton>
              )}
            {userType == "FREE_USER" && !userData?.freeTrialStartDate && (
              <NavButton
                aria-label="menu"
                onClick={this.startFreeTrialHandler}
                color="inherit"
                style={{
                  color: "#fffd02",
                }}
              >
                <UpdateIcon style={{ marginRight: "10px" }} />
                START FREE TRIAL
              </NavButton>
            )}
            {userType == "FREE_USER" &&
              (daysLeftInTrial <= 0 || !userData?.freeTrialStartDate) && (
                <NavButton
                  aria-label="menu"
                  onClick={this.gotoPlan}
                  color="inherit"
                >
                  <UnarchiveIcon style={{ marginRight: "10px" }} />
                  UPGRADE
                </NavButton>
              )}
            <NavButton
              style={{ width: "130px", justifyContent: "flex-start" }}
              onClick={(e) => this.handlePreviewMoreClick(e)}
              color="inherit"
            >
              <VisibilityIcon style={{ marginRight: "10px" }} /> PREVIEW
            </NavButton>
            <Menu
              id="preview-menu"
              anchorEl={this.anchorElPreview}
              keepMounted
              open={Boolean(this.anchorElPreview)}
              onClose={this.handlePreviewMoreClose}
            >
              <MenuItem
                style={{ padding: "6px 0px" }}
                onClick={this.handlePreviewMoreClose}
              >
                <Button
                  style={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={() => this.handleInference("StartLayer")}
                  color="inherit"
                >
                  <VisibilityIcon style={{ marginRight: "10px" }} /> PREVIEW
                  FROM BEGINNING{" "}
                </Button>
              </MenuItem>
              <MenuItem
                style={{ padding: "6px 0px" }}
                onClick={this.handlePreviewMoreClose}
              >
                <Button
                  style={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={() => this.handleInference("SelectedLayer")}
                  color="inherit"
                >
                  <VisibilityIcon style={{ marginRight: "10px" }} /> PREVIEW
                  FROM SELECTED QUESTION
                </Button>
              </MenuItem>
            </Menu>
            <NavButton
              style={{ width: "130px", justifyContent: "flex-start" }}
              onClick={this.openShareDialog}
              color="inherit"
            >
              <PlayArrowIcon
                style={{ transform: "scale(1.2)", marginRight: "10px" }}
              />
              SHARE
            </NavButton>
            <NavButton
              style={{ width: "180px", justifyContent: "flex-start" }}
              onClick={this.gotoNarratives}
              color="inherit"
            >
              <MailIcon style={{ marginRight: "10px" }} />
              VIEW RESPONSES
            </NavButton>
            <SaveButton handleSave={this.saveData} kind="save" />
            <LogoutButton />
          </MobileHideDiv>
        </Toolbar>
      </AppBar>
    );
  }

  renderLoading() {
    return (
      <LoaderContainer>
        <CircularProgress />
      </LoaderContainer>
    );
  }

  renderEditor() {
    return (
      <Provider editor={this._editor}>
        <EditorView />
      </Provider>
    );
  }

  renderSnackbar() {
    return (
      <Snackbar
        open={this._showSnackbar}
        autoHideDuration={3000}
        onClose={() => {
          this._showSnackbar = false;
          this._status = "NORMAL";
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <div>
          {this._snackbarMessage && (
            <Alert severity={this._snackbarType} variant="filled">
              {this._snackbarMessage}
            </Alert>
          )}
        </div>
      </Snackbar>
    );
  }

  render() {
    let linkUrl = this.getSurveyLink();
    return (
      <Root>
        {this.renderNavBar()}
        {this._status == "LOADING" ? this.renderLoading() : this.renderEditor()}
        <ShareDialog
          dialogOpen={this.isShareDialog}
          onDialogClose={() => (this.isShareDialog = false)}
          linkUrl={linkUrl}
          fileId={this.fileId}
          match={this.props.match}
        />
        <SaveDialog
          dialogOpen={this.isSaveDialog}
          onDialogClose={() => (this.isSaveDialog = false)}
          handleSave={this.saveData}
        />
        <SaveDialog
          dialogOpen={this.isSaveDialog}
          onDialogClose={() => (this.isSaveDialog = false)}
          handleSave={this.saveData}
        />
      </Root>
    );
  }
}
